import { useEffect, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { TextInput } from "@/components/ui/Field";
import { Spinner } from "@/components/ui/Spinner";
import {
  getDefaultPrintLocation,
  listPrintLocations,
  type PrintLocation,
} from "@/features/timesheetFlow/timesheetFlowApi";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";

export function PrintLocationDialog({
  open,
  onClose,
  onPrint,
  zIndexClass,
}: {
  open: boolean;
  onClose: () => void;
  onPrint: (locationId: number, copies: number) => Promise<void>;
  /** Über Lightbox/Overlays (z. B. Bild-Kontextmenü). */
  zIndexClass?: string;
}) {
  const layers = useTimesheetFlowLayers();
  const resolvedZIndexClass = zIndexClass ?? layers.overlay;
  const [locations, setLocations] = useState<PrintLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [copies, setCopies] = useState("1");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    void Promise.all([listPrintLocations(), getDefaultPrintLocation().catch(() => null)])
      .then(([list, def]) => {
        setLocations(list);
        const preferred = def?.default_location_id;
        if (preferred != null && list.some((loc) => loc.id === preferred)) {
          setLocationId(preferred);
        } else if (list[0]?.id) {
          setLocationId(list[0].id);
        } else {
          setLocationId(null);
        }
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Druckorte konnten nicht geladen werden.");
      })
      .finally(() => setLoading(false));
  }, [open]);

  const handlePrint = async () => {
    if (locationId == null) return;
    const n = parseInt(copies, 10);
    if (!Number.isFinite(n) || n < 1) {
      setError("Bitte eine gültige Kopienanzahl eingeben.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onPrint(locationId, n);
      onClose();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Druck fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Drucken"
      size="md"
      zIndexClass={resolvedZIndexClass}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button type="button" onClick={() => void handlePrint()} disabled={busy || locationId == null}>
            Drucken
          </Button>
        </div>
      }
    >
      {loading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : null}
      {error ? <p className="mb-3 text-sm text-status-red">{error}</p> : null}
      <label className="mb-3 block text-sm">
        <span className="mb-1 block text-label-secondary">Druckort</span>
        <select
          className="w-full rounded-ios border border-separator bg-card px-3 py-2 text-sm"
          value={locationId ?? ""}
          onChange={(e) => setLocationId(Number(e.target.value) || null)}
        >
          {locations.map((loc) => (
            <option key={loc.id} value={loc.id}>
              {loc.name || `Ort ${loc.id}`}
            </option>
          ))}
        </select>
      </label>
      <TextInput label="Kopien" value={copies} onChange={(e) => setCopies(e.target.value)} />
    </Dialog>
  );
}
